import {
    Absence,
    AbsenceStatus,
    TimeEntryType,
    dateAdd,
    formatAbsenceDayCount,
    formatDate,
    formatWeekDayShort,
    getDateArray,
    getRange,
    isHalfDayEntry,
    parseDateString,
    timeEntryTypeColor,
    toDateString,
} from "@pentacode/core";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "../../../elements/absence-dialog";
import type { AbsenceDialog } from "../../../elements/absence-dialog";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/scroller";
import { app, router } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { type RouteArgs, Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";

@customElement("ptc-employees-absences-single")
export class EmployeesAbsencesSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<id>\d+)\/absences/;

    @routeProperty({ arg: "id", type: Number })
    employeeId: number;

    get routeTitle() {
        return `Abwesenheiten: ${this._employee && this._employee.name}`;
    }

    async handleRoute(_args: RouteArgs, { display_absence, ...rest }: { [prop: string]: string }) {
        await this._load();
        if (display_absence) {
            const absence = this._absences.find((a) => a.id === Number(display_absence));
            if (absence) {
                void this._absenceDialog.show(absence);
            }
            router.params = rest;
        }
    }

    @state()
    private _loading = false;

    @state()
    private _absences: Absence[] = [];

    @state()
    private _displayPastAbsences = false;

    @singleton("ptc-absence-dialog")
    private _absenceDialog: AbsenceDialog;

    private get _employee() {
        return (this.employeeId && app.getEmployee(this.employeeId)) || null;
    }

    private async _load() {
        if (!this._employee) {
            return;
        }

        this._loading = true;
        this._displayPastAbsences = false;

        try {
            this._absences = await app.api.getAbsences({ employee: [this.employeeId] });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _newAbsence(type: TimeEntryType) {
        const created = await this._absenceDialog.show(
            new Absence({
                employeeId: this.employeeId,
                type,
            })
        );
        if (created) {
            void this._load();
        }
    }

    private async _editAbsence(absence: Absence) {
        const edited = await this._absenceDialog.show(absence);
        if (edited) {
            void this._load();
        }
    }

    static styles = [
        shared,
        css`
            .absence {
                border: solid 1px var(--shade-2);
                border-radius: 1em;
                padding-top: 0.5em;
            }

            .days-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                grid-gap: 0.5em 0;
            }

            .day {
                --absence-day-radius: 0.5em;

                text-align: center;
                height: 2em;
                box-sizing: border-box;
                position: relative;
            }

            .day.start,
            .day.middle,
            .day.end {
                border-top: solid 1px var(--color-highlight);
                border-bottom: solid 1px var(--color-highlight);
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            .day.start {
                border-left: solid 1px var(--color-highlight);
                border-top-left-radius: var(--absence-day-radius);
                border-bottom-left-radius: var(--absence-day-radius);
            }

            .day.end {
                border-right: solid 1px var(--color-highlight);
                border-top-right-radius: var(--absence-day-radius);
                border-bottom-right-radius: var(--absence-day-radius);
            }

            .approved .day {
                border: none;
            }

            .day.half-day {
                position: relative;
                background: color-mix(in oklch, var(--color-highlight), transparent);

                &::before {
                    content: "";
                    display: block;
                    width: 100%;
                    height: 100%;
                    inset: 0;
                    position: absolute;
                    background: var(--color-highlight);
                    clip-path: var(--path-diagonal-half);
                    border-radius: inherit;
                    z-index: -1;
                }
            }

            .absence.denied .day,
            .absence.requested .day {
                color: var(--color-highlight);
                background: transparent;
            }

            .absence.denied .day:not(.before):not(.after)::after {
                content: "";
                display: block;
                width: 110%;
                height: 1px;
                background: currentColor;
                position: absolute;
                top: 0;
                bottom: 0;
                margin: auto;
                left: -5%;
            }

            .today {
                background: var(--color-highlight);
                color: var(--color-bg);
                border-radius: 100%;
                padding: 0.1em;

                width: 1.5em;
                height: 1.5em;
                line-height: 1.5em;
                font-weight: bold;
            }

            .approved {
                .start,
                .middle,
                .end {
                    .today {
                        background: var(--color-bg);
                        color: var(--color-highlight);
                    }
                }
            }
        `,
    ];

    render() {
        const emp = this._employee;
        if (!emp) {
            return;
        }
        const today = toDateString(new Date());

        const absences = this._displayPastAbsences ? this._absences : this._absences.filter((a) => a.end > today);

        return html`
            <div class="fullbleed vertical layout">
                <div class="padded horizontal layout border-bottom">
                    <div class="stretch"></div>
                    <button class="slim transparent"><ptc-ui-icon name="icon.action.add"></ptc-ui-icon></button>
                    <ptc-popover class="larger tooltip" hide-on-click>
                        <div class="vertical spacing layout">
                            ${[
                                TimeEntryType.Vacation,
                                TimeEntryType.Sick,
                                TimeEntryType.ChildSick,
                                TimeEntryType.SickInKUG,
                            ].map(
                                (type) => html`
                                    <button
                                        class="slim ghost"
                                        @click=${() => this._newAbsence(type)}
                                        style="--color-highlight: ${timeEntryTypeColor(type)}"
                                    >
                                        <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(type)}"></ptc-ui-icon>
                                        ${app.localized.timeEntryTypeLabel({ type })}
                                    </button>
                                `
                            )}
                        </div>
                    </ptc-popover>
                </div>
                ${!absences.length
                    ? html`
                          <div class="stretch centering vertical layout">
                              <ptc-ui-icon name="icon.time-entry.vacation" class="giant faded"></ptc-ui-icon>
                              <div>Es liegen aktuell keine geplanten Abwesenheiten vor.</div>
                              <button
                                  class="subtle double-margined"
                                  @click=${() => (this._displayPastAbsences = true)}
                                  ?hidden=${this._displayPastAbsences}
                              >
                                  Vergangene Abwesenheiten Anzeigen
                              </button>
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${absences
                                  .sort((a, b) => b.start.localeCompare(a.start))
                                  .map((absence) => {
                                      const { start, end, type, status, dayCount, request, response, notes, entries } =
                                          absence;
                                      const inclusiveEnd = dateAdd(end, { days: -1 });
                                      const renderFrom = getRange(start, "week").from;
                                      const renderTo = getRange(inclusiveEnd, "week").to;
                                      const dates = getDateArray(renderFrom, renderTo);

                                      return html`
                                          <div
                                              class="double-padded double-margined absence click ${status}"
                                              style="--color-highlight: ${timeEntryTypeColor(type)}"
                                              @click=${() => this._editAbsence(absence)}
                                          >
                                              <div class="medium vertically-padded horizontal layout">
                                                  <div class="stretch">
                                                      <strong class="colored-text">
                                                          <ptc-ui-icon
                                                              name="${app.localized.timeEntryTypeIcon(type)}"
                                                          ></ptc-ui-icon>
                                                          ${app.localized.timeEntryTypeLabel({
                                                              type: type,
                                                          })}:
                                                      </strong>
                                                      ${formatWeekDayShort(start)},
                                                      <strong>${formatDate(start)}</strong> -
                                                      ${formatWeekDayShort(inclusiveEnd)},
                                                      <strong>${formatDate(inclusiveEnd)}</strong>
                                                  </div>
                                                  <div class="smaller">
                                                      ${status === AbsenceStatus.Requested
                                                          ? html`
                                                                <div class="orange pill">
                                                                    ${type.includes("sick")
                                                                        ? "Eingereicht"
                                                                        : "Beantragt"}
                                                                </div>
                                                            `
                                                          : status === AbsenceStatus.Denied
                                                            ? html` <div class="red pill">Abgelehnt</div> `
                                                            : html`
                                                                  <div class="pill">
                                                                      <strong
                                                                          >${formatAbsenceDayCount(dayCount)}</strong
                                                                      >
                                                                  </div>
                                                              `}
                                                  </div>
                                              </div>

                                              ${request
                                                  ? html`
                                                        <div
                                                            class="padded vertically-margined text-centering orange box"
                                                        >
                                                            <strong>
                                                                <ptc-ui-icon
                                                                    name="icon.planning.vacation-request"
                                                                ></ptc-ui-icon>
                                                                ${type.includes("sick") ? "Eingereicht" : "Beantragt"}
                                                                am ${formatDate(request.time)}
                                                            </strong>
                                                            ${request.message
                                                                ? html` - <span>"${request.message}"</span> `
                                                                : ""}
                                                        </div>
                                                    `
                                                  : ""}
                                              ${response
                                                  ? html`
                                                        ${status === AbsenceStatus.Denied
                                                            ? html`
                                                                  <div
                                                                      class="padded vertically-margined text-centering red box"
                                                                  >
                                                                      <strong>
                                                                          <ptc-ui-icon
                                                                              name="icon.status.rejected"
                                                                          ></ptc-ui-icon>
                                                                          Abgelehnt von
                                                                          ${response.employeeName || "Unbekannt"} am
                                                                          ${formatDate(response.time)}
                                                                      </strong>
                                                                      ${response.message
                                                                          ? html`
                                                                                -
                                                                                <span>"${response.message}"</span>
                                                                            `
                                                                          : ""}
                                                                  </div>
                                                              `
                                                            : html`
                                                                  <div
                                                                      class="padded vertically-margined text-centering green box"
                                                                  >
                                                                      <strong>
                                                                          <ptc-ui-icon
                                                                              name="icon.status.approved"
                                                                          ></ptc-ui-icon>
                                                                          ${type.includes("sick")
                                                                              ? "Erfasst"
                                                                              : "Genehmigt"}
                                                                          von ${response.employeeName || "Unbekannt"} am
                                                                          ${formatDate(response.time)}
                                                                      </strong>
                                                                      ${response.message
                                                                          ? html`
                                                                                -
                                                                                <span>"${response.message}"</span>
                                                                            `
                                                                          : ""}
                                                                  </div>
                                                              `}
                                                    `
                                                  : ""}
                                              ${notes
                                                  ? html`
                                                        <div class="padded vertically-margined text-centering grey box">
                                                            <ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>
                                                            ${notes}
                                                        </div>
                                                    `
                                                  : ""}

                                              <div class="days-grid">
                                                  <div class="text-centering bold">Mo</div>
                                                  <div class="text-centering bold">Di</div>
                                                  <div class="text-centering bold">Mi</div>
                                                  <div class="text-centering bold">Do</div>
                                                  <div class="text-centering bold">Fr</div>
                                                  <div class="text-centering bold">Sa</div>
                                                  <div class="text-centering bold">So</div>

                                                  ${dates.map((date) => {
                                                      const entry = entries?.find((e) => e.date === date);
                                                      const isHalfDay = isHalfDayEntry(entry);
                                                      return html`
                                                          <div
                                                              class="day centering layout ${classMap({
                                                                  before: date < start,
                                                                  start: date === start,
                                                                  middle: date > start && date < inclusiveEnd,
                                                                  end: date === inclusiveEnd,
                                                                  after: date > inclusiveEnd,
                                                                  "half-day": isHalfDay,
                                                              })}"
                                                          >
                                                              <div class="${date === today ? "today" : ""}">
                                                                  ${parseDateString(date)!.getDate()}
                                                              </div>
                                                          </div>
                                                      `;
                                                  })}
                                              </div>
                                          </div>
                                      `;
                                  })}
                              ${!this._displayPastAbsences
                                  ? html`<div class="centering layout">
                                        <button
                                            class="subtle double-margined"
                                            @click=${() => (this._displayPastAbsences = true)}
                                            ?hidden=${this._displayPastAbsences}
                                        >
                                            Vergangene Abwesenheiten Anzeigen
                                        </button>
                                    </div>`
                                  : nothing}
                          </ptc-scroller>
                      `}
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
